import { snapshot } from "valtio";
import { authCacheManager } from "@features/auth/compat/auth-cache-compat";

import { getBridge } from "./bridge";
import { getAccessTokenExpTimestamp } from "@features/api/osp/credentials";

/**
 * 同步给原生壳（三 Tab / 原生菜单）展示用的用户快照。
 * 禁止包含 accessToken / refreshToken / idToken；仅展示与路由辅助字段。
 */
export type ShellNativeMenuMemberSnapshot = {
  /** 套餐展示名（如 Free / Pro） */
  productName?: string;
  isFree?: boolean;
  creditsAvailable?: number;
  /** 会员状态枚举字符串，可选 */
  status?: string;
};

type ShellNativeUserProfilePayload = {
  userId?: string;
  displayName?: string;
  avatarUrl?: string;
  eoaAddress?: string;
  loginType?: string;
  /**
   * OSP 业务会话过期时间（epoch ms），供原生冷启动 seed 分流。
   * 不含 token；与 `AuthCacheManager` OSP cache `expiresAt` 对齐。
   */
  sessionExpiresAtMs?: number;
  /** @deprecated 使用 member.productName */
  memberName?: string | null;
  isFree?: boolean;
  /** 与 `useCredits` / member.credit.available 对齐 */
  creditsAvailable?: number;
  /** getMember 返回的会员子集，供原生菜单 footer 展示 */
  member?: ShellNativeMenuMemberSnapshot | null;
};

let lastMemberMenuSnapshot: ShellNativeMenuMemberSnapshot | null = null;

function resolveSessionExpiresAtMs(
  accessToken?: string,
  cacheExpiresAt?: number,
): number | undefined {
  if (typeof cacheExpiresAt === "number" && cacheExpiresAt > Date.now()) {
    return cacheExpiresAt;
  }
  if (!accessToken) {
    return undefined;
  }
  try {
    return getAccessTokenExpTimestamp(accessToken);
  } catch {
    return undefined;
  }
}

/** 由 `useMemberUser` 在 member 数据变化时调用，与 owner 快照合并后推送原生。 */
export function setShellNativeMenuMemberSnapshot(
  snapshotMember: ShellNativeMenuMemberSnapshot | null,
): void {
  lastMemberMenuSnapshot = snapshotMember;
  syncShellUserProfileToNativeIfInApp();
}

export function clearShellUserProfileOnNativeIfInApp(): void {
  lastMemberMenuSnapshot = null;
  const bridge = getBridge();
  if (!bridge.isInApp()) {
    return;
  }
  bridge.send("shell-user-profile-sync", { action: "clear" });
}

/**
 * 根据当前 rootStore 快照 + 最近一次 member 查询快照推送到原生；未登录时发 clear。
 */
export function syncShellUserProfileToNativeIfInApp(): void {
  const bridge = getBridge();
  if (!bridge.isInApp()) {
    return;
  }
  // rootStore 是普通 getter 对象，不是 valtio proxy；需对各子 store 分别 snapshot。
  // 运行时 require，避免与 `user.ts` 对本模块的静态 import 形成初始化环。

  const loginModule = require("@/src/store/modules/login");
  const userModule = require("@/src/store/modules/user");
  // 兜底：store 模块仍在 module evaluation 中时（如冷启动 initFromCache → loginComplete 重入本函数）
  // require 会拿到 undefined，此时跳过本次同步；求值结束后的 loginComplete / subscribeKey 会补发。
  if (!loginModule?.LoginStore || !userModule?.UserStore) {
    return;
  }

  const { LoginStore } = loginModule;
  const { UserStore } = userModule;
  const loginSnap = snapshot(LoginStore);
  const userSnap = snapshot(UserStore);
  const ospCache = authCacheManager.getOspLoginCache();
  if (!loginSnap.isLoggedIn || !ospCache?.accessToken) {
    clearShellUserProfileOnNativeIfInApp();
    return;
  }
  const sessionExpiresAtMs = resolveSessionExpiresAtMs(
    ospCache.accessToken,
    ospCache.expiresAt,
  );
  if (!sessionExpiresAtMs || sessionExpiresAtMs <= Date.now()) {
    clearShellUserProfileOnNativeIfInApp();
    return;
  }
  const owner = userSnap.ownerInfo;
  const m = lastMemberMenuSnapshot;
  const profile: ShellNativeUserProfilePayload = {
    userId: owner?.id,
    displayName:
      owner?.name ??
      (owner as { issue_name?: string })?.issue_name ??
      (owner as { issue_email?: string })?.issue_email ??
      undefined,
    avatarUrl: owner?.avatar ?? undefined,
    eoaAddress: loginSnap.eoaAddress ?? userSnap.eoaAddress ?? undefined,
    loginType:
      loginSnap.loginType !== undefined && loginSnap.loginType !== null
        ? String(loginSnap.loginType)
        : undefined,
    sessionExpiresAtMs,
    memberName: m?.productName ?? undefined,
    isFree: m?.isFree,
    creditsAvailable: m?.creditsAvailable,
    member: m
      ? {
          productName: m.productName,
          isFree: m.isFree,
          creditsAvailable: m.creditsAvailable,
          status: m.status,
        }
      : undefined,
  };
  bridge.send("shell-user-profile-sync", { action: "sync", profile });
}
